import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  ArrowLeft, ArrowRight, Calendar, Clock, MapPin, Trophy,
  Home as HomeIcon, Plane, Goal, Square, RefreshCw, Flag as FlagIcon, Star,
} from "lucide-react";

import { Section } from "@/components/site/section";
import { Img } from "@/components/site/img";
import { Flag } from "@/components/site/flag";
import { Crest } from "@/components/site/crest";
import { dateUtils } from "@/components/site/cards";
import { PHOTOS, matchPoster } from "@/assets/photos";
import { fixturesQuery } from "@/lib/site-queries";
import { getFixtureContent, type TimelineEvent } from "@/lib/fixture-content";

const SCOPES = ["senior", "u20", "u17", "u15", "women", "world_cup"] as const;

const CLUB_COMPS = ["primera", "copa libertadores", "copa sudamericana", "argentine cup"];
function isClub(competition: string | null) {
  const c = (competition ?? "").toLowerCase();
  return CLUB_COMPS.some((x) => c.includes(x));
}

export const Route = createFileRoute("/national-team/fixtures_/$id")({
  loader: async ({ context, params }) => {
    // Search across all team scopes so any fixture id resolves.
    const lists = await Promise.all(
      SCOPES.map((s) => context.queryClient.ensureQueryData(fixturesQuery(s, 60))),
    );
    const fixture = lists.flat().find((f) => f.id === params.id) ?? null;
    if (!fixture) throw notFound();
    return { fixture };
  },
  head: ({ loaderData }) => {
    const f = (loaderData as { fixture?: { opponent: string; competition: string | null } } | undefined)?.fixture;
    const title = f ? `Argentina vs ${f.opponent} — ${f.competition ?? "Match"}` : "Match";
    const poster = f ? matchPoster(f.opponent) : null;
    const img = poster ?? PHOTOS.teamHuddle;
    return {
      meta: [
        { title: `${title} | Argentinian.Soccer` },
        { name: "description", content: f ? `${title}. Kickoff, venue, lineups, news and reaction.` : "Match details" },
        { property: "og:title", content: title },
        { property: "og:image", content: img },
        { name: "twitter:image", content: img },
      ],
    };
  },
  notFoundComponent: () => (
    <Section>
      <h1 className="font-display text-4xl text-navy">Fixture not found</h1>
      <p className="mt-2 text-muted-foreground">That match isn’t on our schedule.</p>
      <Link
        to="/national-team/fixtures"
        className="mt-4 inline-flex items-center gap-1 font-condensed text-sm font-bold uppercase tracking-widest text-primary"
      >
        <ArrowLeft className="h-4 w-4" /> All fixtures
      </Link>
    </Section>
  ),
  component: FixtureDetailPage,
});

function FixtureDetailPage() {
  const { fixture: f } = Route.useLoaderData();
  const { data: senior } = useSuspenseQuery(fixturesQuery("senior", 60));
  const related = senior.filter((x) => x.id !== f.id).slice(0, 3);
  const content = getFixtureContent(f);

  const club = isClub(f.competition);
  const poster = club ? null : matchPoster(f.opponent);
  const past = f.argentina_score !== null && f.opponent_score !== null;

  return (
    <>
      {/* HERO — match info */}
      <section className="relative overflow-hidden border-b border-border bg-navy text-primary-foreground">
        <div className="absolute inset-x-0 top-0 h-1 sky-bar" />

        <div className="relative mx-auto max-w-5xl px-4 pt-8 pb-10 lg:px-8 lg:pt-12 lg:pb-16">
          <Link
            to="/national-team/fixtures"
            className="inline-flex items-center gap-1 font-condensed text-xs font-bold uppercase tracking-widest text-sky-deep"
          >
            <ArrowLeft className="h-4 w-4" /> All fixtures
          </Link>

          <p className="mt-4 font-condensed text-xs font-bold uppercase tracking-[0.3em] text-sky-deep">
            {f.competition ?? "Match"}
          </p>

          {content.relatedArticleSlug && (
            <Link
              to="/news/$slug"
              params={{ slug: content.relatedArticleSlug }}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary/90 px-3 py-1 font-condensed text-[0.7rem] font-bold uppercase tracking-widest text-primary-foreground transition-colors hover:bg-primary"
            >
              Read the full article <ArrowRight aria-hidden className="h-3.5 w-3.5" />
            </Link>
          )}

          {/* Teams + score/time */}
          <div className="mt-4 flex items-center justify-between gap-3 sm:gap-6">
            <TeamBlock name="Argentina" side="home" isHome={!!f.is_home} club={club} country="argentina" />
            <div className="text-center">
              {past ? (
                <p className="font-stadium text-5xl leading-none sm:text-7xl">
                  {f.argentina_score} <span className="text-sky">–</span> {f.opponent_score}
                </p>
              ) : (
                <>
                  <p className="font-stadium text-5xl leading-none sm:text-7xl">{dateUtils.fmtTime(f.kickoff)}</p>
                  <p className="mt-1 font-condensed text-[0.65rem] font-bold uppercase tracking-[0.3em] text-primary-foreground/70">
                    UTC kickoff
                  </p>
                </>
              )}
            </div>
            <TeamBlock name={f.opponent} side="away" isHome={!f.is_home} club={club} country={f.opponent} />
          </div>

          {/* Big poster on its own */}
          {poster && (
            <div className="relative mt-8 overflow-hidden rounded-2xl border border-white/10 shadow-stadium">
              <Img
                src={poster}
                alt={`Argentina vs ${f.opponent} match poster`}
                width={1920}
                height={1080}
                priority
                className="aspect-[16/9] w-full object-cover"
              />
            </div>
          )}
        </div>
      </section>

      {/* MATCH FACTS */}
      <Section>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Fact icon={Calendar} label="Date" value={dateUtils.fmtMonthDay(f.kickoff)} />
          <Fact icon={Clock} label="Kick-off" value={`${dateUtils.fmtTime(f.kickoff)} UTC`} />
          <Fact icon={MapPin} label="Venue" value={f.venue ?? "TBC"} />
          <Fact icon={Trophy} label="Competition" value={f.competition ?? "Match"} />
        </div>

        <div className="mt-6 rounded-xl border border-border bg-card p-5">
          <p className="eyebrow">Match brief</p>
          <p className="mt-2 text-base leading-relaxed text-foreground">
            Argentina face <strong className="text-navy">{f.opponent}</strong>
            {f.venue ? ` at ${f.venue}` : ""} on{" "}
            <strong className="text-navy">{dateUtils.fmtLong(f.kickoff)}</strong>. Full coverage —
            lineups, live updates and post-match reaction — will appear on this page as the match
            approaches.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="rounded-sm bg-secondary px-2 py-0.5 font-condensed text-[0.7rem] font-bold uppercase tracking-widest text-secondary-foreground">
              {f.is_home ? (
                <span className="inline-flex items-center gap-1">
                  <HomeIcon className="h-3 w-3" /> Home
                </span>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <Plane className="h-3 w-3" /> Away
                </span>
              )}
            </span>
            {past && (
              <span className="rounded-sm bg-primary px-2 py-0.5 font-condensed text-[0.7rem] font-bold uppercase tracking-widest text-primary-foreground">
                Full-time
              </span>
            )}
          </div>
        </div>
      </Section>

      {/* ARTICLE BODY — lead, storylines, key player */}
      <Section>
        <article className="mx-auto max-w-3xl">
          {content.lead && (
            <p className="font-display text-2xl leading-snug text-navy first-letter:float-left first-letter:mr-2 first-letter:font-stadium first-letter:text-6xl first-letter:leading-[0.85] first-letter:text-primary">
              {content.lead}
            </p>
          )}

          {content.storylines.length > 0 && (
            <>
              <h2 className="mt-8 font-stadium text-3xl tracking-wide text-navy">Storylines</h2>
              <ul className="mt-3 space-y-2 text-base leading-relaxed text-foreground">
                {content.storylines.map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span aria-hidden className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="mt-8 flex items-start gap-3 rounded-xl border border-sand/60 bg-sand/30 p-4">
            <Star aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <p className="font-condensed text-[0.65rem] font-bold uppercase tracking-[0.25em] text-muted-foreground">
                Player to watch
              </p>
              <p className="mt-0.5 font-display text-xl text-navy">{content.keyPlayer.name}</p>
              <p className="font-condensed text-xs uppercase tracking-widest text-muted-foreground">
                {content.keyPlayer.role}
              </p>
              <p className="mt-2 text-sm text-foreground">{content.keyPlayer.note}</p>
            </div>
          </div>

          {content.headToHead.played > 0 && (
            <>
              <h2 className="mt-8 font-stadium text-3xl tracking-wide text-navy">Head to head</h2>
              <div className="mt-3 grid grid-cols-4 overflow-hidden rounded-xl border border-border bg-card text-center">
                <H2HCell label="Played" value={content.headToHead.played} />
                <H2HCell label="Argentina" value={content.headToHead.arg} accent />
                <H2HCell label="Draws" value={content.headToHead.draws} />
                <H2HCell label={f.opponent} value={content.headToHead.opp} />
              </div>
            </>
          )}
        </article>
      </Section>

      {/* LINEUPS */}
      <Section eyebrow="Team sheets" title={past ? "Confirmed lineups" : "Probable lineups"}>
        <div className="grid gap-6 lg:grid-cols-2">
          <LineupCard
            teamName="Argentina"
            country="argentina"
            isClub={false}
            lineup={content.lineups.argentina}
          />
          <LineupCard
            teamName={f.opponent}
            country={f.opponent}
            isClub={club}
            lineup={content.lineups.opponent}
          />
        </div>
      </Section>

      {/* TIMELINE */}
      <Section eyebrow="Match timeline" title={past ? "How it unfolded" : "Live timeline"}>
        <ol className="relative ml-2 border-l-2 border-border">
          {content.timeline.map((ev, i) => (
            <TimelineRow key={i} ev={ev} opponent={f.opponent} />
          ))}
        </ol>
      </Section>

      {/* RELATED ARTICLE CTA */}
      {content?.relatedArticleSlug && (
        <Section>
          <Link
            to="/news/$slug"
            params={{ slug: content.relatedArticleSlug }}
            className="group flex items-center justify-between gap-4 rounded-xl border border-border bg-navy p-5 text-primary-foreground transition-shadow hover:shadow-stadium"
          >
            <div>
              <p className="font-condensed text-[0.65rem] font-bold uppercase tracking-[0.3em] text-sky">
                Full report
              </p>
              <p className="mt-1 font-display text-2xl">Read the full Argentina vs {f.opponent} article</p>
            </div>
            <ArrowRight aria-hidden className="h-6 w-6 shrink-0 transition-transform group-hover:translate-x-1" />
          </Link>
        </Section>
      )}



      {related.length > 0 && (
        <Section eyebrow="More fixtures" title="Around this match">
          <ul className="grid gap-3 sm:grid-cols-3">
            {related.map((r) => (
              <li key={r.id}>
                <Link
                  to="/national-team/fixtures/$id"
                  params={{ id: r.id }}
                  className="block rounded-xl border border-border bg-card p-4 transition-shadow hover:shadow-stadium"
                >
                  <p className="font-condensed text-[0.65rem] font-bold uppercase tracking-[0.25em] text-muted-foreground">
                    {r.competition ?? "Match"}
                  </p>
                  <p className="mt-1 font-display text-lg text-navy">Argentina vs {r.opponent}</p>
                  <p className="mt-1 font-condensed text-xs uppercase tracking-widest text-muted-foreground">
                    {dateUtils.fmtDate(r.kickoff)} · {dateUtils.fmtTime(r.kickoff)} UTC
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </>
  );
}

function TeamBlock({
  name,
  side,
  isHome,
  club,
  country,
}: {
  name: string;
  side: "home" | "away";
  isHome: boolean;
  club: boolean;
  country: string;
}) {
  const align = side === "home" ? "items-start text-left" : "items-end text-right";
  return (
    <div className={`flex flex-1 min-w-0 flex-col gap-2 ${align}`}>
      <div className={`flex items-center gap-2 ${side === "away" ? "flex-row-reverse" : ""}`}>
        {club ? <Crest team={name} /> : <Flag country={country} className="h-8 w-12" />}
        <p className="truncate font-display text-2xl leading-none sm:text-3xl">{name}</p>
      </div>
      <p className="font-condensed text-[0.65rem] font-bold uppercase tracking-[0.25em] text-primary-foreground/65">
        {isHome ? "Home" : "Away"}
      </p>
    </div>
  );
}

function Fact({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="flex items-center gap-1.5 font-condensed text-[0.65rem] font-bold uppercase tracking-[0.25em] text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-primary" aria-hidden /> {label}
      </p>
      <p className="mt-1.5 font-display text-lg leading-tight text-navy">{value}</p>
    </div>
  );
}

function H2HCell({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className={`px-3 py-4 ${accent ? "bg-primary text-primary-foreground" : ""}`}>
      <p className="font-stadium text-3xl leading-none">{value}</p>
      <p className="mt-1 truncate font-condensed text-[0.6rem] font-bold uppercase tracking-[0.2em] opacity-80">
        {label}
      </p>
    </div>
  );
}

function LineupCard({
  teamName,
  country,
  isClub: club,
  lineup,
}: {
  teamName: string;
  country: string;
  isClub: boolean;
  lineup: { formation: string; manager: string; xi: Array<{ num: number; name: string; pos: string }> };
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border bg-secondary/50 px-4 py-3">
        <div className="flex items-center gap-2.5">
          {club ? <Crest team={teamName} /> : <Flag country={country} className="h-6 w-9" />}
          <div>
            <p className="font-display text-lg text-navy">{teamName}</p>
            <p className="font-condensed text-[0.65rem] font-bold uppercase tracking-[0.25em] text-muted-foreground">
              {lineup.manager}
            </p>
          </div>
        </div>
        <span className="rounded-sm bg-primary px-2 py-0.5 font-condensed text-[0.7rem] font-bold uppercase tracking-widest text-primary-foreground">
          {lineup.formation}
        </span>
      </div>
      <ol className="divide-y divide-border">
        {lineup.xi.map((p) => (
          <li key={`${p.num}-${p.name}`} className="flex items-center gap-3 px-4 py-2.5">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-navy font-stadium text-sm text-primary-foreground">
              {p.num}
            </span>
            <span className="flex-1 font-display text-base text-navy">{p.name}</span>
            <span className="font-condensed text-[0.65rem] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {p.pos}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function TimelineRow({ ev, opponent }: { ev: TimelineEvent; opponent: string }) {
  const sideLabel = ev.side === "argentina" ? "Argentina" : ev.side === "opponent" ? opponent : null;
  const Icon =
    ev.type === "goal" ? Goal :
    ev.type === "yellow" || ev.type === "red" ? Square :
    ev.type === "sub" ? RefreshCw :
    ev.type === "ko" || ev.type === "ht" || ev.type === "ft" ? FlagIcon :
    Clock;
  const iconColor =
    ev.type === "goal" ? "bg-primary text-primary-foreground" :
    ev.type === "yellow" ? "bg-[#facc15] text-navy" :
    ev.type === "red" ? "bg-red-600 text-white" :
    ev.type === "sub" ? "bg-emerald-600 text-white" :
    "bg-navy text-primary-foreground";
  return (
    <li className="relative ml-4 py-3 pl-6">
      <span
        aria-hidden
        className={`absolute -left-[1.05rem] top-3.5 grid h-7 w-7 place-items-center rounded-full ring-4 ring-background ${iconColor}`}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-stadium text-lg text-navy">{ev.minute}</span>
        {sideLabel && (
          <span className="font-condensed text-[0.65rem] font-bold uppercase tracking-[0.25em] text-muted-foreground">
            {sideLabel}
          </span>
        )}
      </div>
      <p className="mt-0.5 text-sm leading-relaxed text-foreground">{ev.text}</p>
    </li>
  );
}
